import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowRight, Globe } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import Brand from "@/components/Brand";

export default function ApproveAreaManager() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { t, lang, setLang } = useT();
  const [state, setState] = useState({ status: "loading", message: "", user: null });

  useEffect(() => {
    if (!token) {
      setState({ status: "error", message: t("verify_missing_token") });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.post("/area-managers/approve", { token });
        if (cancelled) return;
        setState({
          status: "success",
          message: res.data?.already_active ? t("approved_already") : t("approved_success"),
          user: res.data?.user || null,
        });
      } catch (err) {
        if (cancelled) return;
        const detail = err.response?.data?.detail;
        setState({ status: "error", message: typeof detail === "string" ? detail : t("approve_failed") });
      }
    })();
    return () => { cancelled = true; };
  }, [token, t]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-pattern px-4 py-6" data-testid="approve-page">
      {/* Prominent language toggle — top corner */}
      <button
        onClick={() => setLang(lang === "ar" ? "en" : "ar")}
        className="fixed top-4 end-4 z-50 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur border border-teal-200 shadow-lg rounded-full px-3 py-1.5 text-xs font-bold text-teal-800 hover:bg-teal-50 hover:border-teal-400 transition"
        data-testid="lang-toggle-approve-top"
        title={t("language")}
        aria-label={t("language")}
      >
        <Globe size={13} /> {lang === "ar" ? "English" : "عربي"}
      </button>
      <div className="w-full max-w-md">
        <Brand variant="hero" className="mb-5 sm:mb-6" />

        <div className="bg-white rounded-2xl card-shadow p-7 text-center" data-testid="approve-result">
          {state.status === "loading" && (
            <>
              <div className="bg-teal-50 text-teal-700 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3 animate-pulse">
                <Loader2 size={32} className="animate-spin" />
              </div>
              <p className="text-sm text-gray-500">{t("approving")}</p>
            </>
          )}
          {state.status === "success" && (
            <>
              <div className="bg-emerald-50 text-emerald-700 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                <CheckCircle2 size={32} />
              </div>
              <h2 className="text-xl font-bold text-emerald-800 mb-2" data-testid="approve-success-title">
                {state.message}
              </h2>
              {state.user && (
                <div className="mt-3 mb-4 inline-block text-start bg-emerald-50/60 border border-emerald-200 rounded-xl px-4 py-3 text-sm text-emerald-900">
                  <div><b>{state.user.full_name}</b></div>
                  <div className="text-xs text-emerald-700" style={{ direction: "ltr" }}>@{state.user.username}</div>
                  <div className="text-xs text-emerald-700 mt-1" style={{ direction: "ltr" }}>{state.user.email}</div>
                </div>
              )}
            </>
          )}
          {state.status === "error" && (
            <>
              <div className="bg-rose-50 text-rose-600 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                <XCircle size={32} />
              </div>
              <h2 className="text-xl font-bold text-rose-700 mb-2" data-testid="approve-error-title">
                {t("approve_failed")}
              </h2>
              <p className="text-sm text-gray-600">{state.message}</p>
            </>
          )}

          {state.status !== "loading" && (
            <Link to="/login" className="btn btn-primary inline-flex mt-5" data-testid="approve-back-link">
              {t("approve_back_to_login")} <ArrowRight size={14} />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
